import { useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import {
  SortPayload,
  useDataProvider,
  ListContextProvider,
  InfinitePaginationContext,
  type InfinitePaginationContextValue,
} from 'react-admin';
import { useInfiniteQuery } from '@tanstack/react-query';
import SkeletonBox from 'components/generic/SkeletonBox';
import { SearchView } from 'components/Search/SearchView';
import { useSearch } from 'components/Search/SearchContextProvider';
import { SavedSearchModal } from 'components/SavedSearch/SavedSearchModal';
import { useWireSearch } from './WireSearchWithContext';
import { SearchItemsProvider } from 'components/Search/components/SearchItemsProvider';
import { SearchViewResults } from 'components/Search/components/SearchAdvance/SearchAdvancedView';
import { SearchAdvancedTopBar } from 'components/Search/components/SearchAdvance/SearchAdvancedTopBar';
import {
  WireFilterFields,
  WirePopoverFields,
  WirePostSearchContent,
  WirePreSearchContent,
  WireSearchDropdown,
} from 'components/Search/components/SearchAdvance/components/Syndication/syndication';
import { checkSearchEnabled } from 'components/Search/functions';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';

export const WireSearchView = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { validListFilter } = useSearch();
  const { channels, channelsLoading, channelOptions } = useWireSearch();

  const [debouncedListFilter] = useDebouncedValue(validListFilter, 400);

  const raw_query = channelOptions?.find(
    (option) => option.type === 'string' && option.name === 'raw_query',
  );

  const {
    sort: debouncedSort,
    dir: debouncedOrder,
    ...debouncedFilter
  } = debouncedListFilter ?? {};

  /**
   * (RA v5 update) ra-core/src/dataProvider/useInfiniteGetList.ts explicitly omits
   * and hardcodes getNextPageParam with numeric increment — it cannot be overridden
   * via queryOptions.
   *
   * Instead we replace InfiniteListBase with direct useInfiniteQuery plus dataProvider.getList()
   * for cursor-based resources. Provides full control over getNextPageParam.
   */
  const {
    data,
    isPending,
    isFetching,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      'wire/channel/search',
      'getInfiniteList',
      { filter: debouncedFilter, sort: debouncedSort, dir: debouncedOrder },
    ],
    queryFn: async ({ pageParam }) =>
      dataProvider.getList('wire/channel/search', {
        pagination: { page: pageParam, perPage: 50 },
        sort: { field: debouncedSort, order: debouncedOrder } as SortPayload,
        filter: debouncedFilter,
      }),
    initialPageParam: '',
    getNextPageParam: (lastPage: any) => lastPage?.pageInfo?.nextPage ?? undefined,
    getPreviousPageParam: (lastPage: any) => lastPage?.pageInfo?.prevPage ?? undefined,
    enabled: checkSearchEnabled({
      filter: debouncedFilter,
      resource: 'wire/channel/search',
    }).isEnabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const flatData = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.pages.flatMap((page) => page.data ?? []);
  }, [data]);

  if (!debouncedListFilter || !validListFilter || channelsLoading) {
    return (
      <SkeletonBox
        text="Loading Wire Search..."
        width="100%"
        height="100%"
      />
    );
  }

  if (channels.length === 0) {
    return (
      <SkeletonBox
        text="No channels are available for this site"
        width="100%"
        height="100%"
      />
    );
  }

  // ListContextProvider and InfinitePaginationContext.Provider replace what
  // InfiniteListBase previously provided to children automatically.
  //
  // ListContextProvider supplies the flattened page data and react-query hook state
  // to consumers via useListContext.
  // InfinitePaginationContext supplies fetchNextPage/fetchPreviousPage and pagination
  // state to consumers via useInfinitePaginationContext.
  return (
    <ListContextProvider
      value={
        {
          data: flatData,
          isPending,
          isFetching,
          hasNextPage,
          hasPreviousPage,
          refetch,
          resource: 'wire/channel/search',
        } as any // RA's ListControllerResult requires ~20 fields and types isPending as literal false - we supply only what downstream consumers use
      }
    >
      <InfinitePaginationContext.Provider
        value={
          {
            hasNextPage,
            fetchNextPage,
            isFetchingNextPage,
            hasPreviousPage,
            fetchPreviousPage,
            isFetchingPreviousPage,
          } as unknown as InfinitePaginationContextValue // fetchNextPage/fetchPreviousPage return GetListResult, but RA expects GetInfiniteListResult (adds pageParam) - shapes are compatible
        }
      >
        <SearchItemsProvider>
          <SearchView
            searchInputs={
              <SearchAdvancedTopBar
                useRawQuery={Boolean(raw_query)}
                preSearchComponent={<WirePreSearchContent />}
                postSearchComponent={<WirePostSearchContent />}
                searchDropdownComponent={<WireSearchDropdown />}
                advancedPopoverFieldsComponent={<WirePopoverFields />}
                advancedFilterFieldsComponent={<WireFilterFields />}
              />
            }
            searchResults={<SearchViewResults />}
            savedSearchModal={
              <SavedSearchModal addPayload={{ query: { app: ['wire'] } }} />
            }
          />
        </SearchItemsProvider>
      </InfinitePaginationContext.Provider>
    </ListContextProvider>
  );
};
